/**
 * Class description
 *
 * Created: 2019-08-30
 * @author jonas
 */
import {Timeline, TimelineLite, TweenLite, Linear, RoughEase} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {ParsedGameData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedGameData";
import {SpacierArcadeParsedGameData} from "../server/data/SpacierArcadeParsedGameData";
import {SpacierArcadeGameModuleConfig} from "../SpacierArcadeGameModuleConfig";
import {SpacierArcadeModeData} from "../server/data/SpacierArcadeModeData";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {SASymbols} from "../symbols/SpacierArcadeReelSymbol";
import {SpacierArcadeGameConfig} from "../SpacierArcadeGameConfig";
import {GameSetting} from "@nolimitcity/slot-game/bin/core/setting/GameSetting";
import {SpacierArcade} from "../SpacierArcade";
import {GameRoundPresentation} from "@nolimitcity/slot-game/bin/timelinegameround/core/gameround/GameRoundPresentation";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {SpacierArcadeGameMode} from "../gamemode/SpacierArcadeGameMode";
import {SpacierArcadeWildSymbol} from "../symbols/SpacierArcadeWildSymbol";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {SpacierArcadeStickySymbol} from "../symbols/SpacierArcadeStickySymbol";
import {SAHelper} from "../utils/SAHelper";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {SpacierArcadeSoundConfig} from "../SpacierArcadeSoundConfig";
import {GameRoundManager} from "@nolimitcity/slot-game/bin/timelinegameround/core/gameround/GameRoundManager";

export class FreeSpinWildAttention implements GameRoundPresentation {
    private readonly _reelId:number;
    private readonly _duration:number;
    private readonly _abortDuration:number;
    private _tl:TimelineLite;
    private _layer:ScalableLayer;
    stagger?:number;
    abortStagger?:number;

    constructor() {
        this._layer = StageManager.getLayer(SpacierArcadeGameConfig.instance.LAYERS.SYMBOLS_ATTENTION.name);
    }

    public prepare(data:SpacierArcadeParsedGameData):void {
        if(data.mode === SpacierArcadeGameMode.FREESPIN){
            this.stagger = ((data.payoutMultiplier - 1) * 0.4) + 3.2;
        }
    }

    public getAnimation(data:SpacierArcadeParsedGameData):Timeline | null {
        this._tl = new TimelineLite();
        this._tl.add([
            SpacierArcadeGameModuleConfig.saFreespinReelAttentionView.playNearWin(2),
            () => SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.FS_REELS_HIGHLIGHT)
        ]);
        this._tl.add(SpacierArcadeGameModuleConfig.saFreespinReelAttentionView.playWildPosition(data.fsWildPositions), "-=1.0");
        this._tl.add(() => {
            let wildPos:number[][] = data.fsWildPositions;
            for(let i:number = 0; i < wildPos.length; i++) {
                let multiplier: number = data.overlayWildsMultipliers[wildPos[i][0]][wildPos[i][1]];
                this.animateWildMultiplier(multiplier, wildPos[i], data.spinIntensity);
            }
        }, "+=0.5");
        this._tl.add(() => SpacierArcadeGameModuleConfig.saFreespinReelAttentionView.stopNearWin(), "+=1.0");
        if(data.mode === SpacierArcadeGameMode.FREESPIN && data.nextMode !== SpacierArcadeGameMode.FREESPIN_RESPIN){
            this._tl.to({}, 2.0, {});
        }
        return this._tl;
    }

    private animateWildMultiplier(multi:number, wildPos:number[], spinIntensity:number):Timeline{
        const tl:TimelineLite = new TimelineLite();
        if(multi > 0){
            let wildSymbol:SpacierArcadeWildSymbol = <SpacierArcadeWildSymbol>SpacierArcadeGameModuleConfig.spacierArcadeOverlayView.getStickySymbol(wildPos[0], wildPos[1],  multi, SASymbols.W, spinIntensity, SpacierArcadeGameConfig.instance.SYMBOL_STATES.fsStickyWild).stickySymbol;
            wildSymbol.finalMultiplier = multi;
            tl.add([
                () => SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.FS_WILD_ENTER),
                wildSymbol.changeState({state: SpacierArcadeGameConfig.instance.SYMBOL_STATES.fsStickyWild})
            ]);
            const intTl:TimelineLite = new TimelineLite();
            if(multi > 1) {
                for (let i = 1; i <= multi; i++) {
                    if (i === multi) {
                        intTl.add([
                            () => wildSymbol.multiplier = i,
                            () => SlotGame.sound.playEffect(SpacierArcadeSoundConfig.instance.WILD_MULTI_FINAL),
                            wildSymbol.changeState({
                                state: SpacierArcadeGameConfig.instance.SYMBOL_STATES.winMulti
                            })], (i - 1) * 0.3);
                        intTl.add(TweenLite.fromTo(StageManager.stage, 0.4, {x: -0.5, y: -0.5}, {
                            x: 0.5,
                            y: 0.5,
                            ease: new RoughEase({
                                strength: 8,
                                points: 10,
                                template: Linear.easeNone,
                                randomize: false
                            }),
                            clearProps: "x"
                        }), (i - 1) * 0.3);
                    }
                    else {
                        intTl.add([
                            () => wildSymbol.multiplier = i,
                            () => SlotGame.sound.playEffect("wildMultiX" + (i % 3 === 0 ? "3" : i)),
                            wildSymbol.changeState({
                                state: SpacierArcadeGameConfig.instance.SYMBOL_STATES.multiplier
                            })], (i - 1) * 0.3);
                    }
                }
            }
            intTl.add([
                () => wildSymbol.changeState({
                state: SpacierArcadeGameConfig.instance.SYMBOL_STATES.sticky
                })
            ]);
            tl.add(intTl);
        }
        return tl;
    }

    public abort(isActive:boolean, data:SpacierArcadeParsedGameData):Timeline | null {
        if(isActive && this._tl) {
            GameRoundManager.detachAndPlayTimeline(this._tl);
            //Creating a new timeline with the same length as the time left on the active one, to make sure that the master timeline does not end before this one.
            const newTl = new TimelineLite();
            newTl.add(new TweenLite(this, this._tl.duration() - this._tl.time(), {}));
            return newTl;
        }
        return null;
        // return this.getAnimation(data);
    }
}